import { SmileIcon } from "lucide-react"
import { Button } from "@components/common/button"
import { useRef, useState } from "react"
import EmojiPicker from "emoji-picker-react"
import { useMutation } from "@tanstack/react-query"
import { DictType } from "@pma/types"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  ADD_FILES_TO_NOTE_ENDPOINT,
  DIGITAL_OCEAN_FILES_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"
import axios from "axios"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import useTranslator from "@hooks/use-translator"
import { MentionUser } from "./mention-user"
import { toast } from "@hooks/use-toast"
import { useModal } from "@stores/modal-store"
import { useAuth } from "@stores/auth-store"
import { cn, getUserFromId } from "@pma/utils/helpers"
import AddFileToNote from "./add-file-to-note"

interface Props {
  task: DictType
  reply?: boolean
  focus?: boolean
  setShowReply?: any
  fromExpandCalling?: boolean
  setUpdateRecord?: any
  setIsSubmitting?: (value: boolean) => void
}
export default function NoteTextArea({
  task,
  reply,
  setShowReply,
  focus,
  fromExpandCalling = false,
  setUpdateRecord,
  setIsSubmitting,
}: Props) {
  const modal = useModal()
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const noteRef = useRef<DictType | null>()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)
  const organizationUsers = authConfig?.organizationUsers

  const [message, setMessage] = useState("")
  const [file, setFile] = useState<[any] | []>([])
  const [openEmoji, setOpenEmoji] = useState(false)
  const [note, setNote] = useState<DictType | null>(null)

  const addNote = async (contextId: string, contextType: string, note: string) => {
    const response = await axios.post(
      SERVER_URL + "/notes/add",
      {
        note: note,
        context_id: contextId,
        context_type: contextType,
      },
      {
        headers: {
          Authorization: `Bearer ${parseCookies()[ACCESS_TOKEN]}`,
        },
      },
    )

    return response.data
  }

  const addNoteMutation = useMutation({
    mutationKey: ["add-new-note", task?.id],
    mutationFn: (note: string) => addNote(task?.id, "task", note),
    onSuccess: (data) => {
      clearEditor()

      if (file.length > 0) {
        // Upload file workflow
        setNote(data)
        noteRef.current = data
        uploadFileMutation.mutate()
      }
      if (fromExpandCalling) {
        queryClient.invalidateQueries({ queryKey: ["get-notes-for-expand"] })
        queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      } else {
        // Trigger a Revalidate tasks/projects
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
        // queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      }
      if (setUpdateRecord) {
        setUpdateRecord(true)
      }
      setIsSubmitting?.(false)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "User not invited") {
        promptReInvite(message)
      }
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
      setIsSubmitting?.(false)
    },
  })

  const replyMessage = async (message: string) => {
    const response = await axios.post(
      SERVER_URL + "/notes/add",
      {
        note: message,
        context_id: 0,
        context_type: "task",
        reply_to: task?.id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  }

  const replyMessageMutation = useMutation({
    mutationKey: [`reply-${task?.id}`],
    mutationFn: (message: string) => replyMessage(message),
    onSuccess: (data) => {
      clearEditor()
      if (file.length > 0) {
        // Upload file workflow
        setNote(data)
        noteRef.current = data
        uploadFileMutation.mutate()
      }
      if (fromExpandCalling) {
        queryClient.invalidateQueries({ queryKey: ["get-notes-for-expand"] })
        queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      } else {
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
        // queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      }
      setIsSubmitting?.(false)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "User not invited") {
        promptReInvite(message)
      }

      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
      setIsSubmitting?.(false)
    },
  })

  const addToFileNote = async (files: any[]) => {
    const file_ids = files?.map((f) => {
      return f.id
    })
    const response = await axios.post(
      SERVER_URL + ADD_FILES_TO_NOTE_ENDPOINT,
      {
        note_id: noteRef.current?.id,
        inform_users: true,
        files_id: file_ids,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return response.data
  }

  const addFileToNoteMutation = useMutation({
    mutationKey: ["add-file-to-note"],
    mutationFn: addToFileNote,
    onSuccess: (data) => {
      // Reset file
      setFile([])
      if (fromExpandCalling) {
        queryClient.invalidateQueries({ queryKey: ["get-notes-for-expand"] })
        queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      } else {
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
        // queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  const uploadFile = async () => {
    const formData = new FormData()

    if (!file) {
      return
    }

    if (file.length > 0) {
      ;[...file].forEach((val) => {
        formData.append("files", val)
      })
    }

    const url = `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`

    const response = await axios.post(url, formData, {
      headers: {
        accept: "*/*",
        "Content-Type": "multipart/form-data",
      },
    })

    return response?.data
  }

  const uploadFileMutation = useMutation({
    mutationKey: ["upload-file"],
    mutationFn: uploadFile,
    onSuccess: (data) => {
      const payload = data as any[]

      // Link digital ocean file to  note
      addFileToNoteMutation.mutate(payload)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to upload file at this time"),
        variant: "error",
      })
    },
  })

  function formatMentions(input: string) {
    const mentionRegex = /\@\[([^\]]+)\]\((\d+)\)/g

    const formattedOutput = input.replace(mentionRegex, (match, name, id) => {
      return `@${id}#`
    })

    return formattedOutput
  }

  const promptReInvite = (message: string) => {
    const regex = /@(\d+)#/
    const match = formatMentions(message).match(regex)
    const userId = match ? String(match[1]) : ""

    const user = getUserFromId(userId, organizationUsers)
    modal.onOpen()
    modal.setContext("invite")

    modal.setMetaData(user)
  }

  const onSubmit = () => {
    if (message === "") {
      return
    }

    setIsSubmitting?.(true)

    if (reply) {
      return replyMessageMutation.mutate(formatMentions(message))
    } else {
      return addNoteMutation.mutate(formatMentions(message))
    }
  }

  const clearEditor = () => {
    const editor = document?.getElementById(task.id)?.children || []
    if (editor.length > 0) {
      editor[0].innerHTML = "<p><br></p>"
    }

    setMessage("")
    if (setShowReply) {
      setShowReply(false)
    }
  }

  return (
    <div className='mb-2 max-w-[330px] rounded-lg md:max-w-[335px]'>
      <MentionUser
        quillId={task.id}
        message={message}
        setMessage={setMessage}
        reply={reply}
        focus={!!focus}
      />

      <div className='relative flex w-full items-center justify-between space-x-2 border-y border-gray-200 p-1 dark:border-gray-800'>
        {/* Emoji Picker */}
        <Button
          className='p-1 text-xs text-gray-600 dark:text-gray-400'
          variant='ghost'
          onClick={() => setOpenEmoji((prev) => !prev)}
        >
          <span className='sr-only'>Emoji</span>
          <SmileIcon className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} /> {translate("Emoji")}
        </Button>
        <div className={cn("absolute z-30 h-[100px]", isHebrew ? "right-0" : "left-0")}>
          <EmojiPicker
            open={openEmoji}
            onEmojiClick={(e: any) => {
              setMessage((prev) => prev + e.emoji)
              setOpenEmoji(false)
            }}
            className=''
          />
        </div>

        <div className='flex flex-1 justify-end gap-2'>
          <AddFileToNote context='note' setFile={setFile} reset={file.length < 0} />
          <Button
            size={"sm"}
            onClick={onSubmit}
            disabled={message === "" || addNoteMutation.isPending}
            loading={addNoteMutation.isPending}
            className='h-7 w-[70px]'
          >
            {reply ? translate("Reply") : translate("Update")}{" "}
          </Button>
        </div>
      </div>
    </div>
  )
}
